/**
 * Configuración global de la aplicación.
 *
 * Envuelve todo con:
 *   - React Query: guarda en memoria las respuestas del backend, reintenta las
 *     que fallan y evita pedir dos veces lo mismo.
 *   - El router: las rutas de la aplicación.
 *   - Los avisos flotantes.
 */

import { useEffect, useState } from "react";
import { MutationCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "react-router-dom";
import { AvisoDeVersion } from "./AvisoDeVersion";
import { Avisos } from "@/components/Avisos";
import { SoftfiError } from "@/lib/api";
import { LimiteDeError } from "@/components/PantallaDeError";
import { registrarQueryClient } from "@/lib/cambioDeNegocio";
import { recordarLasConsultas, recuperarLasConsultas } from "@/lib/memoriaDeConsultas";
import { router } from "./router";
import { useAuth } from "@/stores/auth";
import { useConexion } from "@/stores/conexion";
import { useImpresora } from "@/stores/impresora";
import { useOffline } from "@/stores/offline";

// ═══════════════════════════════════════════════════════════════════════════
//  ⚠️ LA CAMPANITA SE REFRESCA DESPUÉS DE CUALQUIER CAMBIO
// ═══════════════════════════════════════════════════════════════════════════
//
// El fallo, tal cual pasó: el dueño actualiza la tasa del bolívar, la pantalla
// de Monedas dice «actualizada hoy»... y la campanita sigue con el aviso rojo
// de «la tasa de VES lleva 4 días sin actualizar». Solo se iba recargando la
// página.
//
// Y ahí no se pierde un adorno: se pierde la campanita entera. Un aviso que
// sigue encendido después de haberlo resuelto enseña que los avisos no son de
// fiar, y a partir de ese momento no se miran — incluido el día que avisa de
// algo que sí importa.
//
// Se arregla aquí y no en la pantalla de Monedas a propósito. Los avisos
// dependen de media aplicación: las tasas viejas, el stock bajo, las ventas
// sin enviar, el fiado vencido. Ir apagándolos uno a uno desde cada pantalla
// es una lista que nadie mantiene al día, y el que se olvide vuelve a dejar un
// aviso encendido para siempre. Cualquier cosa que MODIFIQUE algo puede haber
// resuelto un aviso, así que después de cualquier cambio se vuelven a pedir.
//
// Cuesta una petición pequeña por cada acción, y solo cuando hubo una acción.
const cacheDeMutaciones = new MutationCache({
  onSuccess: () => {
    void queryClient.invalidateQueries({ queryKey: ["avisos"] });
  },
});

const queryClient: QueryClient = new QueryClient({
  mutationCache: cacheDeMutaciones,
  defaultOptions: {
    queries: {
      // Los datos se consideran frescos 30 segundos. Evita que cambiar de
      // pestaña vuelva a pedir todo el catálogo cada vez.
      staleTime: 30_000,

      // No reintentar errores del usuario (404, 403, 422): reintentarlos no
      // cambia nada y solo hace parecer lenta la aplicación. Sí reintentar
      // fallos de red, que en un negocio con internet inestable son constantes.
      retry: (intentos, error) => {
        if (error instanceof SoftfiError) {
          if (error.status >= 400 && error.status < 500) return false;
        }
        return intentos < 2;
      },

      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false, // Nunca reintentar algo que MODIFICA datos: podría duplicarlo
    },
  },
});

// Se registra al cargar el módulo y no dentro de un efecto: el store de sesión
// puede necesitar vaciar la caché en el primer render (alguien que entra con la
// sesión de otro negocio guardada), y para entonces un efecto todavía no corrió.
registrarQueryClient(queryClient);

export function Providers() {
  const restaurarSesion = useAuth((s) => s.restaurarSesion);
  const tenantId = useAuth((s) => s.tenant?.id ?? null);

  // Al abrir la aplicación: si hay una sesión guardada, la recuperamos sin
  // pedir la contraseña de nuevo.
  useEffect(() => {
    void restaurarSesion();
  }, [restaurarSesion]);

  /**
   * ⚠️ LO ÚLTIMO QUE SE VIO DE CADA PANTALLA, ANTES DE DIBUJAR NADA.
   *
   * ═══════════════════════════════════════════════════════════════════════
   *  POR QUÉ ESTO BLOQUEA EL PRIMER DIBUJO
   * ═══════════════════════════════════════════════════════════════════════
   *
   * Porque llegar tarde no sirve de nada. Una pantalla que se dibuja primero
   * lanza su petición, falla por falta de señal y enseña «no se pudo cargar».
   * Meterle los datos treinta milisegundos después ya no borra ese error: el
   * cajero ya lo leyó y ya salió a buscar señal.
   *
   * Se espera a leer de IndexedDB, que son decenas de milisegundos, y solo la
   * primera vez que se sabe de qué negocio se trata. A cambio, sin internet la
   * aplicación abre con todo lo que se vio la última vez en vez de con una
   * pantalla de error por sección.
   *
   * ⚠️ Y si falla, se sigue igual. `recuperarLasConsultas` no lanza nunca: en
   * el peor caso devuelve `null` y Softfi arranca como arrancaba antes.
   */
  const [memoriaLista, setMemoriaLista] = useState(false);
  useEffect(() => {
    if (!tenantId) return;
    let vivo = true;
    setMemoriaLista(false);

    void recuperarLasConsultas(queryClient, tenantId).then(() => {
      if (vivo) setMemoriaLista(true);
    });

    return () => {
      vivo = false;
    };
  }, [tenantId]);

  // Y a partir de ahí, se va guardando sola.
  useEffect(() => {
    if (!tenantId || !memoriaLista) return;
    return recordarLasConsultas(queryClient, tenantId);
  }, [tenantId, memoriaLista]);

  /**
   * RECONEXIÓN DE LA IMPRESORA TÉRMICA.
   *
   * Si este dispositivo ya tenía una impresora emparejada, el navegador recuerda
   * el permiso: la volvemos a tomar en silencio, sin diálogos ni avisos. Así el
   * cajero abre Softfi por la mañana y la primera venta ya imprime sola.
   *
   * Si la impresora está apagada, no pasa nada: no se conecta y se reintentará
   * cuando alguien imprima. Que falle la impresora nunca puede frenar la app.
   */
  useEffect(() => {
    void useImpresora.getState().iniciar();
  }, []);

  /**
   * VIGILANCIA DE LA CONEXIÓN Y ENVÍO AUTOMÁTICO.
   *
   * Aquí se cierra el círculo del modo sin conexión: en el momento en que el
   * internet vuelve, las ventas guardadas se envían solas. El cajero no tiene que
   * acordarse de nada ni pulsar ningún botón — que es justo lo que no va a hacer
   * en medio de un día de trabajo.
   */
  useEffect(() => {
    const dejarDeVigilar = useConexion.getState().iniciarVigilancia();

    // Al abrir la app: contar lo pendiente y, si hay internet, enviarlo.
    void useOffline
      .getState()
      .refrescarContadores()
      .then(() => {
        const { pendientes, clientes } = useOffline.getState();
        // ⚠️ También los clientes: uno registrado sin señal es lo que la venta
        // de esa misma tarde va a nombrar, y sin esto se quedaba en la cola
        // hasta que hubiera además una venta pendiente.
        if (useConexion.getState().enLinea && pendientes + clientes > 0) {
          void useOffline.getState().sincronizar({ silencioso: true });
        }
      });

    // Y cada vez que la conexión pasa de "no hay" a "sí hay".
    const dejarDeEscuchar = useConexion.subscribe((estado, anterior) => {
      if (estado.enLinea && !anterior.enLinea) {
        void useOffline.getState().sincronizar();
      }
    });

    return () => {
      dejarDeVigilar();
      dejarDeEscuchar();
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Avisos />
      {/*
        El límite va DENTRO del proveedor de consultas y FUERA del router, a
        propósito: dentro, para que la pantalla de error pueda seguir usando
        React Query si hace falta; fuera del router, para que atrape también los
        fallos del propio enrutado.
      */}
      {/*
        ⚠️ FUERA del límite de error, y a propósito.

        Si la aplicación revienta, el aviso de «hay versión nueva» es justo lo
        que puede salvar la situación: muchas veces el fallo YA está arreglado y
        lo único que hace falta es recargar. Metido dentro, se caería con todo
        lo demás y la persona se quedaría mirando una pantalla rota sin saber
        que la solución era un botón.
      */}
      <AvisoDeVersion />

      <LimiteDeError>
        {/*
          ⚠️ Con sesión, se espera a la memoria de consultas antes del primer
          dibujo. Ver el efecto de arriba: llegar tarde con esos datos es no
          llegar.

          Sin sesión se dibuja ya: la pantalla de entrar no tiene nada que
          recordar, y hacerla esperar por una lectura que no le sirve sería
          media pantalla en blanco por nada.
        */}
        {!tenantId || memoriaLista ? <RouterProvider router={router} /> : null}
      </LimiteDeError>
    </QueryClientProvider>
  );
}

export { queryClient };
